import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Stack, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getMyTickets, getAllTickets } from '../../api/supportApi';
import { CATEGORIES, STATUSES, STATUS_COLOR, PP, fmt, age } from './supportConstants';

const headSx = {
    fontFamily: PP, fontWeight: 700, fontSize: '0.74rem', color: '#475569',
    bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap',
};
const cellSx = { fontFamily: PP, fontSize: '0.82rem', color: '#1E293B' };

/* ============================================================================
   TICKETS — a table, and a row opens its own page

   Same shape as the events list, for the same reason: a queue is something you
   scan down and compare, and the thread is too long to live in a panel beside it.
   (user 2026-10-03: "all ticket make it table. click to view another page")

     /support      My Tickets   — the caller's own, scoped by the API
     /support/all  All Tickets  — the desk, route-guarded AND right-checked

   Open ones sit at the top, oldest first: a ticket that has been waiting longest
   is the one that needs answering, so "worst first" is the right order for a
   queue even though it is the opposite of a feed.
   ========================================================================= */
export default function SupportList({ deskScope = false }) {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = deskScope
                ? await getAllTickets({ status: statusFilter || undefined, category: categoryFilter || undefined })
                : await getMyTickets();
            setTickets(res.data || []);
        } catch {
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [deskScope, statusFilter, categoryFilter]);

    useEffect(() => { load(); }, [load]);

    const ordered = useMemo(() => {
        const settled = t => t.status === 'Resolved' || t.status === 'Closed';
        const open = tickets.filter(t => !settled(t));
        const done = tickets.filter(settled);
        // Longest-waiting first among the open ones; most recently touched first
        // among the settled, since those are only ever looked back at.
        open.sort((a, b) => (b.ageHours || 0) - (a.ageHours || 0));
        done.sort((a, b) => new Date(b.updatedOn || b.createdOn) - new Date(a.updatedOn || a.createdOn));
        return { rows: [...open, ...done], openCount: open.length, waiting: open.filter(t => t.awaitingReply).length };
    }, [tickets]);

    const shown = ordered.rows.slice(page * perPage, page * perPage + perPage);

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            <Stack direction="row" alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        {deskScope ? 'All Tickets' : 'My Tickets'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>
                        {deskScope
                            ? `${ordered.openCount} open · ${ordered.waiting} waiting on a reply from us`
                            : 'Anything not working, or not clear — ask here and it goes to the people who can fix it.'}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                {deskScope && (
                    <>
                        <Select size="small" displayEmpty value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                            sx={{ minWidth: 140, fontFamily: PP, fontSize: '0.8rem' }}>
                            <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.8rem' }}>Every status</MenuItem>
                            {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{s}</MenuItem>)}
                        </Select>
                        <Select size="small" displayEmpty value={categoryFilter}
                            onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
                            sx={{ minWidth: 150, fontFamily: PP, fontSize: '0.8rem' }}>
                            <MenuItem value="" sx={{ fontFamily: PP, fontSize: '0.8rem' }}>Every category</MenuItem>
                            {CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontFamily: PP, fontSize: '0.8rem' }}>{c}</MenuItem>)}
                        </Select>
                    </>
                )}
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/support/new')}
                    sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                    Create ticket
                </Button>
            </Stack>

            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headSx}>#</TableCell>
                                <TableCell sx={headSx}>Subject</TableCell>
                                {/* Who it is about, which is not who raised it. A desk reading
                                    a list scans by customer. (user 2026-10-04) */}
                                <TableCell sx={headSx}>Customer</TableCell>
                                {deskScope && <TableCell sx={headSx}>Raised by</TableCell>}
                                <TableCell sx={headSx}>Category</TableCell>
                                <TableCell sx={headSx}>Priority</TableCell>
                                <TableCell sx={headSx}>Status</TableCell>
                                {deskScope && <TableCell sx={headSx}>Assigned</TableCell>}
                                <TableCell sx={headSx} align="right">Age</TableCell>
                                <TableCell sx={headSx}>Last activity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && (
                                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={24} />
                                </TableCell></TableRow>
                            )}
                            {!loading && shown.length === 0 && (
                                <TableRow><TableCell colSpan={10} align="center" sx={{ ...cellSx, py: 5, color: '#94A3B8' }}>
                                    {deskScope ? 'Nothing in the queue.' : 'You have not asked anything yet.'}
                                </TableCell></TableRow>
                            )}
                            {shown.map(t => {
                                const [bg, fg] = STATUS_COLOR[t.status] || STATUS_COLOR.Closed;
                                return (
                                    <TableRow key={t.supportTicketId} hover
                                        onClick={() => navigate(`/support/${t.supportTicketId}`)}
                                        sx={{
                                            cursor: 'pointer',
                                            borderLeft: t.priority === 'High' ? '3px solid #DC2626' : '3px solid transparent',
                                        }}>
                                        <TableCell sx={{ ...cellSx, color: '#94A3B8', fontWeight: 700 }}>
                                            {t.supportTicketId}
                                        </TableCell>
                                        <TableCell sx={{ ...cellSx, fontWeight: 600, maxWidth: 320 }}>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <Box component="span" sx={{
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>{t.subject}</Box>
                                                {t.awaitingReply && (
                                                    <Chip label="Waiting" size="small"
                                                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#FEE2E2', color: '#991B1B' }} />
                                                )}
                                                {t.wasReopened && (
                                                    <Chip label="Reopened" size="small"
                                                        sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }} />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            {t.sale ? (
                                                <Box>
                                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                                        {t.sale.customerName}
                                                    </Box>
                                                    <Typography sx={{ fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}>
                                                        #{t.sale.salesId}
                                                        {t.sale.energy ? ` · ${t.sale.energy} kWp` : ''}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box component="span" sx={{ color: '#CBD5E1' }}>—</Box>
                                            )}
                                        </TableCell>
                                        {deskScope && <TableCell sx={cellSx}>{t.raisedBy}</TableCell>}
                                        <TableCell sx={{ ...cellSx, color: '#64748B' }}>{t.category}</TableCell>
                                        <TableCell sx={{
                                            ...cellSx,
                                            color: t.priority === 'High' ? '#DC2626' : '#64748B',
                                            fontWeight: t.priority === 'High' ? 700 : 400,
                                        }}>{t.priority}</TableCell>
                                        <TableCell>
                                            <Chip label={t.status} size="small"
                                                sx={{ height: 20, fontSize: '0.64rem', fontWeight: 700, bgcolor: bg, color: fg }} />
                                        </TableCell>
                                        {deskScope && (
                                            <TableCell sx={{ ...cellSx, color: t.assignedToName ? '#1E293B' : '#94A3B8' }}>
                                                {t.assignedToName || 'unassigned'}
                                            </TableCell>
                                        )}
                                        <TableCell sx={cellSx} align="right">{age(t.ageHours)}</TableCell>
                                        <TableCell sx={{ ...cellSx, color: '#64748B' }}>{fmt(t.updatedOn || t.createdOn)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={ordered.rows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={e => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{
                        borderTop: '1px solid #E2E8F0',
                        // The two labels are <p> elements and pick up a page-wide paragraph
                        // margin, which drops them off the line the select and the arrows sit
                        // on. Styling every descendant with '& *' changed the font but left
                        // that margin, so the row read crooked. Same shape as payoutReport.
                        '& .MuiTablePagination-toolbar': {
                            fontFamily: PP, fontSize: '0.78rem', minHeight: 44, alignItems: 'center',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            margin: 0, fontFamily: PP, fontSize: '0.78rem', lineHeight: 1.6,
                        },
                        '& .MuiTablePagination-select': { fontFamily: PP, fontSize: '0.78rem' },
                        '& .MuiTablePagination-actions button': { padding: '6px' },
                    }}
                />
            </Paper>
        </Box>
    );
}
